import { useTierStatus } from '@/hooks/useTierStatus'
import { BILLING_PLANS_PATH, getFreeTrialPageSetupState } from '@/utils/freeTierPageSetup'

/**
 * Whether this account can start Page Setup on a page it does not already
 * manage, and where a control should send them if it cannot.
 *
 * Generation became a subscription feature, and the "generate this page"
 * controls are scattered across the portfolio grid, the findings lists and the
 * page detail panels. Each one used to link straight at /generate, which for a
 * free account means a button that promises setup and delivers an upgrade
 * panel. Centralised here so the rule is written once: six copies of a pricing
 * check is six chances for one of them to be wrong after the next pricing
 * change.
 *
 * Deliberately NOT applied to regenerating a page the account already manages.
 * urlIsAlreadyManaged permits that on any plan, and blocking it would take away
 * something they already have rather than declining to give more.
 */
export function usePageSetupAccess() {
  const { entitlements, isLoading, isEntitlementsKnown } = useTierStatus()

  /**
   * Fails open. Blocking requires positive evidence that this account cannot
   * generate, never merely the absence of evidence: the tier query is disabled
   * until team context resolves, and its fallback assumes 'free', so a plain
   * "not loading" check would show the paywall to a paying customer on every
   * surface that renders before their plan arrives. The server enforces the
   * real limit either way, so guessing wrong in this direction costs nothing.
   */
  const needsSubscription =
    isEntitlementsKnown && !isLoading && getFreeTrialPageSetupState(entitlements).isAtLimit

  return {
    needsSubscription,

    /** Where a "set this page up" control should actually point. */
    setupHref: (path: string) => (needsSubscription ? BILLING_PLANS_PATH : path),

    /** What that control should say, so the label matches where it goes. */
    setupLabel: (label: string) => (needsSubscription ? 'See plans' : label),
  }
}
